import { useEffect, useMemo, useRef, useState } from 'react';
import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import { AddCircleOutline, AttachFile, Send } from '@material-ui/icons';
import { Warning } from '@carbon/icons-react';
import { checkBypass } from '@utils/bypass';
import { CustomizedTypography } from '@components/Customized';
import { clsx, colorSet, useSnackbar } from '@components/Provider';
import useStyles from '../../useStyles';

interface Props {
  matches: boolean;
  isDisabled: boolean;
  isWithdraw: boolean;
  loading: boolean;
  onSend: (message: string, isBypassSuspected: boolean) => Promise<void>;
  onOpenUploadBox: () => void;
}

interface BypassEventPayload {
  bypassSuspect: boolean;
}

const ChatMessageInputPresenter = ({
  matches,
  isDisabled,
  isWithdraw,
  loading,
  onSend,
  onOpenUploadBox
}: Props) => {
  const classes = useStyles();
  const messageRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<boolean>(false);
  const [isBypassSuspected, setIsBypassSuspected] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const placeholder = useMemo(() => {
    switch (true) {
      case loading:
        return '채팅 정보를 가져오는 중입니다.';
      case isWithdraw:
        return '메세지를 입력할 수 없습니다.';
      case isDisabled:
        return '상담이 종료되었습니다.';
      default:
        return '메세지를 입력해주세요';
    }
  }, [loading, isWithdraw, isDisabled]);

  const sendMessage = async () => {
    if (!messageRef.current) return;
    const message = messageRef.current.value.trim();
    messageRef.current.value = '';

    if (!message) {
      setError(true);
      return;
    }

    try {
      await onSend(message, isBypassSuspected);
    } catch (e) {
      enqueueSnackbar('채팅 전송에 실패했습니다');
      messageRef.current.value = message;
    }
  };

  useEffect(() => {
    if (isBypassSuspected) {
      window.dispatchEvent(
        new CustomEvent<BypassEventPayload>('bypassChat', {
          detail: { bypassSuspect: true }
        })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent<BypassEventPayload>('bypassChat', {
          detail: { bypassSuspect: false }
        })
      );
    }
  }, [isBypassSuspected]);

  return (
    <>
      {isBypassSuspected && (
        <div className={classes['Chat-Input-Bypass-Warning-Bar-root']}>
          <Warning color={colorSet.errorMain} size={20} />
          <CustomizedTypography className="warning-text" variant="body2">
            개별적으로 연락처를 교환하여 캐파 외부에서 거래할 경우 분쟁 발생시
            책임소재를 밝히기가 어려우니 유의해 주세요.
          </CustomizedTypography>
        </div>
      )}
      <div className={classes['Chat-Message-Input-root']}>
        <div className="message-buttons">
          <IconButton disabled={isDisabled} onClick={onOpenUploadBox}>
            <AttachFile fontSize="small" />
          </IconButton>
        </div>
        <div className="message-input">
          {matches && (
            <IconButton
              size="small"
              disabled={isDisabled}
              onClick={onOpenUploadBox}
            >
              <AddCircleOutline fontSize="small" />
            </IconButton>
          )}
          <TextField
            variant="outlined"
            onKeyPress={async (e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                await sendMessage();
              }
            }}
            placeholder={placeholder}
            InputProps={{
              classes: {
                notchedOutline: clsx('inputBorder', {
                  noBorder: matches ? false : true
                }),
                multiline: 'multiline'
              },
              endAdornment: (
                <InputAdornment
                  position="end"
                  disablePointerEvents={isDisabled}
                >
                  {matches ? (
                    <IconButton size="small" onClick={sendMessage}>
                      <Send fontSize="small" />
                    </IconButton>
                  ) : (
                    <div className="inputEndIcon" onClick={sendMessage}>
                      <CustomizedTypography
                        variant="body2"
                        fontWeight="500"
                        color="inherit"
                      >
                        전송
                      </CustomizedTypography>
                    </div>
                  )}
                </InputAdornment>
              )
            }}
            onChange={(e) => {
              checkBypass(
                e.target.value,
                () => setIsBypassSuspected(true),
                () => setIsBypassSuspected(false)
              );
            }}
            inputProps={{
              ref: messageRef
            }}
            error={error}
            disabled={isDisabled}
            multiline
            minRows={matches ? 1 : 4}
            maxRows={matches ? 1 : 4}
            fullWidth
          />
        </div>
      </div>
    </>
  );
};

export default ChatMessageInputPresenter;
